import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Footer, MarketingHeader, SEO } from '@/components';
import { track } from '@/lib/analytics';
import {
  BLOG_POSTS,
  blogImageFrameClass,
  blogPostPath,
  formatBlogDate,
  getBlogImage,
} from '@/lib/blog';

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://harborcoparent.com/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Blog',
      item: 'https://harborcoparent.com/blog',
    },
  ],
};

const blogJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: 'Harbor Blog',
  description:
    'What’s new in Harbor, the co-parenting app, and how each feature works.',
  url: 'https://harborcoparent.com/blog',
  publisher: {
    '@type': 'Organization',
    name: 'Harbor',
    url: 'https://harborcoparent.com',
  },
  blogPost: BLOG_POSTS.map((post) => ({
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    url: `https://harborcoparent.com${blogPostPath(post.slug)}`,
  })),
};

const jsonLdScripts = [breadcrumbJsonLd, blogJsonLd];

function BlogIndexPage() {
  useEffect(() => {
    track('Blog Screen');
  }, []);

  const [latest, ...rest] = BLOG_POSTS;
  const latestImage = getBlogImage(latest);

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='What’s New in Harbor | Harbor Blog'
        description='Updates from Harbor, the co-parenting app: messaging, shared expenses, the custody calendar, Temperature Check, and more.'
        path='/blog'
        jsonLd={jsonLdScripts}
      />
      {jsonLdScripts.map((data) => (
        <script
          key={String(data['@type'])}
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(data).replace(/</g, '\\u003c'),
          }}
        />
      ))}
      <MarketingHeader />

      <main className='mx-auto max-w-4xl px-6 pt-32 pb-20'>
        <h1 className='text-4xl font-bold tracking-tight text-gray-900 md:text-5xl'>
          What’s new in Harbor
        </h1>
        <p className='mt-4 max-w-2xl text-xl leading-relaxed text-gray-600'>
          What we’ve built for co-parents, and how it works.
        </p>

        <Link
          to='/blog/$slug'
          params={{ slug: latest.slug }}
          onClick={() =>
            track('Click Blog Post', {
              slug: latest.slug,
              location: 'Blog Featured',
            })
          }
          className={`group mt-12 grid items-center gap-8 rounded-3xl bg-primary/5 p-8 ${
            latestImage ? 'md:grid-cols-2' : ''
          }`}
        >
          <div>
            <p className='text-sm text-gray-500'>
              <time dateTime={latest.publishedAt}>
                {formatBlogDate(latest.publishedAt)}
              </time>
            </p>
            <h2 className='mt-3 text-3xl font-bold leading-tight text-gray-900 group-hover:text-primary'>
              {latest.title}
            </h2>
            <p className='mt-4 text-lg leading-relaxed text-gray-600'>
              {latest.excerpt}
            </p>
          </div>
          {latestImage ? (
            <img
              src={latestImage.src}
              alt={latest.imageAlt ?? latest.title}
              className={`mx-auto h-auto max-h-80 w-auto object-contain ${blogImageFrameClass(
                latestImage
              )}`}
            />
          ) : null}
        </Link>

        <ul className='mt-12 divide-y divide-gray-200 border-t border-gray-200'>
          {rest.map((post) => (
            <li key={post.slug}>
              <Link
                to='/blog/$slug'
                params={{ slug: post.slug }}
                onClick={() =>
                  track('Click Blog Post', {
                    slug: post.slug,
                    location: 'Blog',
                  })
                }
                className='group grid gap-2 py-7 md:grid-cols-[180px_1fr] md:gap-8'
              >
                <time
                  dateTime={post.publishedAt}
                  className='text-sm text-gray-500 md:pt-1'
                >
                  {formatBlogDate(post.publishedAt)}
                </time>
                <div>
                  <h2 className='text-xl font-bold text-gray-900 group-hover:text-primary'>
                    {post.title}
                  </h2>
                  <p className='mt-2 leading-relaxed text-gray-600'>
                    {post.excerpt}
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </main>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/blog/')({
  component: BlogIndexPage,
});
